import React, { useMemo } from 'react';
import { addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import GlossaryForm from './GlossaryForm';
import { useGlossary, useCreateGlossary, useUpdateGlossary } from '@/hooks/queries/useGlossaries';
import { GlossaryFormData } from './schemas';
import { CreateGlossaryRequest, UpdateGlossaryRequest } from '@/services/apis/glossaries';

interface GlossaryFormContainerProps {
  glossaryId?: string | null;
  onSuccess: () => void;
  onCancel?: () => void;
}

const GlossaryFormContainer: React.FC<GlossaryFormContainerProps> = ({ glossaryId, onSuccess, onCancel }) => {
  const { t } = useTranslation('admin');

  const { data: glossary, isLoading: isLoadingGlossary } = useGlossary({
    id: glossaryId || '',
    shouldSkip: !glossaryId,
  });

  const createGlossaryMutation = useCreateGlossary();
  const updateGlossaryMutation = useUpdateGlossary();

  const defaultValues = useMemo<Partial<GlossaryFormData> | undefined>(() => {
    if (!glossary || isLoadingGlossary) return undefined;

    const englishTranslation = glossary.translations?.find((t) => t.language === 'en');

    return {
      name: glossary.name || '',
      categoryId: glossary.category?.id || '',
      aliases: glossary.aliases || [],
      tags: glossary.tags || [],
      definition: englishTranslation?.definition || '',
    };
  }, [glossary, isLoadingGlossary]);

  const handleSubmit = async (data: GlossaryFormData) => {
    try {
      if (glossaryId) {
        await updateGlossaryMutation.mutateAsync({ id: glossaryId, data: data as UpdateGlossaryRequest });
        addToast({
          title: t('glossaries.actions.updateSuccess'),
          color: 'success',
        });
      } else {
        await createGlossaryMutation.mutateAsync(data as CreateGlossaryRequest);
        addToast({
          title: t('glossaries.actions.createSuccess'),
          color: 'success',
        });
      }
      onSuccess();
    } catch (error) {
      addToast({
        title: glossaryId ? t('glossaries.actions.updateError') : t('glossaries.actions.createError'),
        color: 'danger',
      });
    }
  };

  const isSubmitting = createGlossaryMutation.isPending || updateGlossaryMutation.isPending;

  return (
    <GlossaryForm
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      isLoadingGlossary={isLoadingGlossary}
    />
  );
};

export default GlossaryFormContainer;

